import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Button from './Button';
import { I18nContext } from 'utils/I18n/context';

const sizeMap = { large: 'lg', small: 'sm', extraLarge: 'xl' };

const CustomModal = ({
                         visible = false,
                         loading = false,
                         confirmDisabled = false,
                         className = '',
                         bsSize,
                         areNoControls,
                         isForm,
                         confirmName,
                         title,
                         children,
                         onClose,
                         onConfirm,
                         onSubmit,
                     }) => {
    const ctx = useContext(I18nContext) || {};
    const tModal = ctx.t?.modal ?? { defaultTitle: 'Modal' };
    const tActions = ctx.t?.actions ?? { confirm: 'Confirm', cancel: 'Cancel' };

    const handleClose = useCallback(() => onClose?.(), [onClose]);
    const handleConfirm = useCallback(() => {
        if (!loading && !confirmDisabled) onConfirm?.();
    }, [loading, confirmDisabled, onConfirm]);

    const modalSize = useMemo(() => {
        if (!bsSize) return undefined;
        return sizeMap[bsSize] || bsSize;
    }, [bsSize]);

    return (
        <Modal
            show={visible}
            backdrop="static"
            size={modalSize}
            onHide={handleClose}
            className={className}
            restoreFocus={false}
        >
            <form onSubmit={onSubmit ?? null}>
                <Modal.Header closeButton={false}>
                    <button type="button" className="close" onClick={handleClose}>
                        <span aria-hidden="true">×</span>
                        <span className="sr-only">{tModal.close || 'Close'}</span>
                    </button>
                    <h4 className="modal-title">{title || tModal.defaultTitle}</h4>
                </Modal.Header>
                <Modal.Body>{children}</Modal.Body>
                {!areNoControls ? (
                    <Modal.Footer>
                        {isForm ? (
                            <Button
                                type="submit"
                                extraClass={{ 'apply-btn': true }}
                                loading={loading}
                                disabled={loading || confirmDisabled}
                                text={confirmName || tActions.confirm}
                            />
                        ) : (
                            <Button
                                extraClass={{ 'apply-btn': true }}
                                loading={loading}
                                disabled={loading || confirmDisabled}
                                text={confirmName || tActions.confirm}
                                onClick={handleConfirm}
                            />
                        )}
                        <Button
                            text={tActions.cancel}
                            extraClass={{ 'cancel-btn': true }}
                            onClick={handleClose}
                        />
                    </Modal.Footer>
                ) : null}
            </form>
        </Modal>
    );
};

CustomModal.propTypes = {
    visible: PropTypes.bool,
    loading: PropTypes.bool,
    confirmDisabled: PropTypes.bool,
    className: PropTypes.string,
    bsSize: PropTypes.oneOfType([
        PropTypes.oneOf(['sm', 'lg', 'xl']),
        PropTypes.oneOf(['small', 'large', 'extraLarge']),
    ]),
    areNoControls: PropTypes.bool,
    isForm: PropTypes.bool,
    confirmName: PropTypes.string,
    title: PropTypes.string,
    children: PropTypes.node,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    onSubmit: PropTypes.func,
};

export default CustomModal;
